import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI, trackActivity } from '../../services/api';
import ProjectDetail from './components/ProjectDetail';
import {
  PROJECT_STATUSES, PRIORITIES, STATUS_LABEL, STATUS_COLOUR, PRIORITY_COLOUR, errMsg,
} from './constants';
import './ProjectsApp.css';

const emptyNew = {
  name: '', project_type: '', owner_id: '', client: '', platform: '',
  due_date: '', priority: 'medium', description: '',
};

const ProjectsApp = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState({ clients: [], platforms: [], project_types: [] });

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(emptyNew);
  const [saving, setSaving] = useState(false);

  const userName = useCallback((id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.full_name || u.email) : (id ? `#${id}` : '—');
  }, [users]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (search.trim()) params.q = search.trim();
      if (includeArchived) params.include_archived = true;
      const data = await projectsAPI.listProjects(params);
      setProjects(data);
    } catch (err) {
      setError(errMsg(err, 'Failed to load projects'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, includeArchived]);

  useEffect(() => {
    trackActivity('app_access', 'projects');
    projectsAPI.listUsers().then(setUsers).catch(() => {});
    projectsAPI.getSuggestions().then(setSuggestions).catch(() => {});
    // Deep link from email: ?project=<id>
    const sp = new URLSearchParams(window.location.search);
    const pid = sp.get('project');
    if (pid) setSelectedId(parseInt(pid, 10));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newForm.name.trim()) { setError('Project name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...newForm,
        owner_id: newForm.owner_id ? parseInt(newForm.owner_id, 10) : null,
        due_date: newForm.due_date || null,
      };
      const created = await projectsAPI.createProject(payload);
      setShowNew(false);
      setNewForm(emptyNew);
      await load();
      setSelectedId(created.id);
    } catch (err) {
      setError(errMsg(err, 'Failed to create project'));
    } finally {
      setSaving(false);
    }
  };

  if (selectedId) {
    return (
      <ProjectDetail
        projectId={selectedId}
        users={users}
        suggestions={suggestions}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div className="projects-app">
      <div className="projects-header">
        <div>
          <h4 className="projects-title">Projects</h4>
          <span className="projects-subtitle">RFPs, due diligence, implementations, model builds & research</span>
        </div>
        <button className="proj-btn proj-btn-primary" onClick={() => { setNewForm(emptyNew); setShowNew(true); }}>
          <i className="bi bi-plus-lg" /> New Project
        </button>
      </div>

      {error && <div className="proj-alert proj-alert-danger">{error}<button onClick={() => setError(null)}>×</button></div>}

      <div className="projects-filters">
        <input className="proj-input" placeholder="Search projects…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 240 }} />
        <select className="proj-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <label className="proj-check">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
        <span className="projects-count">{projects.length} project{projects.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="proj-empty">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="proj-empty">No projects yet. Create one to get started.</div>
      ) : (
        <div className="projects-table-wrap">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Project</th><th>Status</th><th>Owner</th><th>Client</th><th>Platform</th>
                <th>Due</th><th>Tasks</th><th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className={p.is_archived ? 'is-archived' : ''} onClick={() => setSelectedId(p.id)}>
                  <td className="proj-name-cell">{p.name}{p.project_type && <span className="proj-type-chip">{p.project_type}</span>}</td>
                  <td><span className="proj-badge" style={{ background: STATUS_COLOUR[p.status] }}>{STATUS_LABEL[p.status] || p.status}</span></td>
                  <td>{userName(p.owner_id)}</td>
                  <td>{p.client || '—'}</td>
                  <td>{p.platform || '—'}</td>
                  <td>{p.due_date ? p.due_date.slice(0, 10) : '—'}</td>
                  <td className="text-center">{p.open_task_count ?? 0}/{p.task_count ?? 0}</td>
                  <td><span className="proj-prio" style={{ color: PRIORITY_COLOUR[p.priority] }}>{p.priority}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <div className="proj-modal-overlay" onClick={() => setShowNew(false)}>
          <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
            <div className="proj-modal-header"><h5>New Project</h5><button onClick={() => setShowNew(false)}>×</button></div>
            <div className="proj-modal-body">
              <label className="proj-field"><span>Name *</span>
                <input className="proj-input" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} autoFocus />
              </label>
              <div className="proj-field-row">
                <label className="proj-field"><span>Type</span>
                  <input className="proj-input" list="proj-types" value={newForm.project_type}
                    onChange={(e) => setNewForm({ ...newForm, project_type: e.target.value })} placeholder="RFP, Due Diligence…" />
                  <datalist id="proj-types">{suggestions.project_types.map((t) => <option key={t} value={t} />)}</datalist>
                </label>
                <label className="proj-field"><span>Owner</span>
                  <select className="proj-select" value={newForm.owner_id} onChange={(e) => setNewForm({ ...newForm, owner_id: e.target.value })}>
                    <option value="">— Me —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                  </select>
                </label>
              </div>
              <div className="proj-field-row">
                <label className="proj-field"><span>Client</span>
                  <input className="proj-input" list="proj-clients" value={newForm.client}
                    onChange={(e) => setNewForm({ ...newForm, client: e.target.value })} placeholder="Any client (free text)" />
                  <datalist id="proj-clients">{suggestions.clients.map((c) => <option key={c} value={c} />)}</datalist>
                </label>
                <label className="proj-field"><span>Platform</span>
                  <input className="proj-input" list="proj-platforms" value={newForm.platform}
                    onChange={(e) => setNewForm({ ...newForm, platform: e.target.value })} placeholder="Any platform (free text)" />
                  <datalist id="proj-platforms">{suggestions.platforms.map((c) => <option key={c} value={c} />)}</datalist>
                </label>
              </div>
              <div className="proj-field-row">
                <label className="proj-field"><span>Due date</span>
                  <input type="date" className="proj-input" value={newForm.due_date} onChange={(e) => setNewForm({ ...newForm, due_date: e.target.value })} />
                </label>
                <label className="proj-field"><span>Priority</span>
                  <select className="proj-select" value={newForm.priority} onChange={(e) => setNewForm({ ...newForm, priority: e.target.value })}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
              </div>
              <label className="proj-field"><span>Description</span>
                <textarea className="proj-input" rows={3} value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} />
              </label>
            </div>
            <div className="proj-modal-footer">
              <button className="proj-btn" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="proj-btn proj-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create Project'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsApp;
